import {MimeType} from '@coolcolduk/enum';
import {EtsyListing, mapEtsyListingToCreateDraftListingRequest} from '@coolcolduk/etsy';
import {Dropzone, useMessage} from '@coolcolduk/ui-mui';
import {throwNewError} from '@coolcolduk/util';
import {Button, Stack} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import {EtsyBulkCreateCsvItemInput, QueueJobContent} from '../../../__generated__/resolvers-types';
import EtsyListingTemplateSelector from '../../../components/etsy/EtsyListingTemplateSelector';
import {JobListButton} from '../../../components/jobList/JobListButton';
import useSetEtsyBulkCreateCsv from '../../../hook/useSetEtsyBulkCreateCsv';
import {FeatureProps} from '../../../types/FeatureObject';
import {errorMap} from '../../../util/error/errorMap';
import {downloadCreateListingCsv} from '../../../util/etsy/csv/downloadCreateListingCsv';
import {zEtsyBulkCreateCsvData} from '../../../util/etsy/csv/getEtsyBulkCreateCsvHeader';
import mapCsvToCreateEtsyListing from '../../../util/etsy/csv/mapCsvToCreateEtsyListing';
import parseFileAsCsv from '../../../util/etsy/csv/parseFileAsCsv';

export interface EtsyBulkCreateCsvProps extends FeatureProps {}

export default function EtsyBulkCreateCsv({selectedAccount}: EtsyBulkCreateCsvProps) {
  const [templateListing, setTemplateListing] = useState<EtsyListing | undefined>();
  const {createBulkEtsyCreateCsvJob} = useSetEtsyBulkCreateCsv();
  const [csvFile, setCsvFile] = useState<File | undefined>();
  const {showMessage} = useMessage();
  const [loading, setLoading] = useState(false);

  // load all templates
  // submit job

  return (
    <Stack direction="column" spacing={2}>
      <JobListButton content={QueueJobContent.etsyBulkCreateCsv} />

      <Button variant="contained" onClick={() => downloadCreateListingCsv([])}>
        Download CSV template
      </Button>

      <Dropzone
        titleText="Bulk upload CSV"
        files={csvFile ? [csvFile] : []}
        setFiles={async (f) => {
          setCsvFile(f[0]);
        }}
        withPreview
        acceptedFileTypes={[MimeType.TEXT_CSV, MimeType.APPLICATION_VND_MS_EXCEL]}
        maxFiles={1}
      />

      <EtsyListingTemplateSelector
        selectedAccount={selectedAccount}
        selectedListing={templateListing}
        setSelectedListing={setTemplateListing}
      />

      <Button
        variant="contained"
        color="primary"
        loading={loading}
        onClick={async () => {
          // load csv file
          // match and build listing to upload
          // submit job
          if (!templateListing) {
            showMessage('Please select a template', undefined, 'warning');
            return;
          }
          if (!csvFile) {
            showMessage('Please select a CSV file', undefined, 'warning');
            return;
          }
          setLoading(true);

          try {
            // load csv data
            const csvData = await throwNewError(
              () => parseFileAsCsv(csvFile, zEtsyBulkCreateCsvData),
              (e) => {
                console.error(e);
                return new Error(`Error parsing CSV file`);
              },
            );

            // map csv data to listing info
            const listingInfo = csvData.map((i) => mapCsvToCreateEtsyListing(i));

            // map listing info to create draft listing request
            const items: EtsyBulkCreateCsvItemInput[] = listingInfo.map((i) => ({
              etsyListingStringified: JSON.stringify(
                mapEtsyListingToCreateDraftListingRequest({...templateListing, tags: i.tags, title: i.title}),
              ),
              sku: i.sku || null,
              images: i.images,
            }));

            try {
              const result = await createBulkEtsyCreateCsvJob(selectedAccount.id, items);
              const hasError = !!result.error;
              const errorMessage = result.error
                ? get(errorMap, result.error.message, `Job added failed`)
                : 'Upload job added';

              showMessage(errorMessage, undefined, hasError ? 'error' : 'success');
            } catch (e) {
              console.error(e);
              showMessage('Job added failed', undefined, 'error');
            }
            setCsvFile(undefined);
          } catch (e: any) {
            showMessage(e.message, undefined, 'error');
          }
          setLoading(false);
        }}
        disabled={!templateListing || !csvFile}
      >
        Add job
      </Button>
    </Stack>
  );
}
